/**
 * Outcome assembly: a matched structure plus its phrase resolutions → one
 * `ParsedCommand`; a structure diagnostic → public `ParseDiagnostics`
 * (plan §3's assemble stage).
 *
 * @remarks
 * Pure mapping, no decisions: the grammar decided the shape, resolution
 * decided the refs, ranking decided the confidence — this module only fits
 * them into the public contract. Preposition-bound phrases land on the field
 * their intent DECLARED (`bindsAs: 'tool'` → `toolRef`; `'indirect_object'`
 * → `indirectObjectRef`) — role assignment stays authored, never guessed.
 *
 * The diagnostic mapping (plan §2.1): `object_required` / `object_forbidden`
 * / `dangling_preposition` / `second_*` collapse into
 * `structure_not_understood` (the consumer teaches from `unmatchedTokens`);
 * `no_intent` → `no_intent_recognized`; `empty_clause` → `blank_input`;
 * `unbound_preposition` keeps its own kind — it is the one failure where the
 * player used a real word the vocabulary knows, just not here.
 *
 * @packageDocumentation
 */

import { type CanonicalToken, type IntentCanonicalToken } from '../grammar/canonicalize-tokens.js';
import { type ClauseStructure, type StructureDiagnostic } from '../grammar/match-clause-structure.js';
import { type ResolvedEntityPhrase } from '../resolve/resolve-entity-phrase.js';
import {
	type IntentToken,
	type ParsedCommand,
	type ParseDiagnostics,
	type PronounResolution,
	type SourceSpan,
	type TokenCorrection,
} from '../types.js';

/** The structural minimum a token needs for original-surface restoration (D26). */
interface SpannedSurface {
	readonly sourceSpan: SourceSpan;
	readonly sourceText: string;
}

/**
 * Quote the PLAYER, not the fuzzy pass (D26): map failure-quoted tokens back
 * to what was actually typed. A correction patches the token stream so a
 * repaired word can re-parse — correct behavior when the clause then
 * SUCCEEDS, gaslighting when it fails ("weather right now" must never be
 * echoed as "weather light now"). Span-keyed, so duplicate words cannot
 * cross-restore.
 *
 * @param tokens - The tokens a diagnostic wants to quote (possibly patched surfaces)
 * @param corrections - Every correction applied to the clause
 * @returns The surfaces to quote — original text wherever a correction touched the span
 */
export const restoreOriginalSurfaces = (
	tokens: readonly SpannedSurface[],
	corrections: readonly TokenCorrection[],
): readonly string[] =>
	tokens.map(
		(token) =>
			corrections.find((correction) => correction.sourceSpan.start === token.sourceSpan.start)?.originalText ??
			token.sourceText,
	);

/** The per-role phrase resolutions a matched structure needs (absent = the structure carved no such phrase). */
export interface ClausePhraseResolutions {
	/** The preposition-bound phrase's resolution (`with the keycard`). */
	readonly boundResolution?: ResolvedEntityPhrase;
	/**
	 * D227: the destination color after a bearing (`go up THE ROAD`), resolved
	 * so a misnamed thing fails honestly. D231: and then KEPT — it rides the
	 * command as its direct object beside the bearing, because the consumer
	 * decides what a bearing with a named thing means (`enter the door` is a
	 * question about the door, not about `in`). D227 set it aside; the
	 * gatehouse's sealed door is why it no longer is.
	 */
	readonly destinationResolution?: ResolvedEntityPhrase;
	/** The direct-object phrase's resolution. */
	readonly objectResolution?: ResolvedEntityPhrase;
}

/**
 * Assemble one clause's `ParsedCommand` from its pipeline products.
 *
 * @param clauseText - The clause, folded form (for transcripts and echoes)
 * @param structure - The matched grammatical reading
 * @param resolutions - The per-role phrase resolutions the structure requires
 * @param confidence - The composed clause confidence (plan §6.1)
 * @param corrections - The fuzzy corrections applied to this clause (plan §6.2); defaults to none
 * @returns The public command proposal
 * @throws Error when a resolution the structure requires is missing (programmer error — the orchestrator resolves before building)
 */
export const buildParsedCommand = (
	clauseText: string,
	structure: ClauseStructure,
	resolutions: ClausePhraseResolutions,
	confidence: number,
	corrections: readonly TokenCorrection[] = [],
): ParsedCommand => {
	const { boundResolution, destinationResolution, objectResolution } = resolutions;
	const pronounResolutions = collectPronounResolutions(resolutions);
	const baseCommand: ParsedCommand = {
		clauseText,
		confidence,
		corrections,
		intent: intentTokenOf(structure),
		intentSurface: intentSurfaceOf(structure),
		qualifiers: [...(objectResolution?.qualifiers ?? []), ...(boundResolution?.qualifiers ?? [])],
		...particleFieldOf(structure),
		...(pronounResolutions.length > 0 ? { pronounResolutions } : {}),
	};

	switch (structure.kind) {
		case 'direction_only': {
			return { ...baseCommand, direction: structure.directionToken.direction };
		}
		// D231: the thing named after the bearing is the command's direct
		// object, when the structure carved one — a set (a plural) is read as
		// nothing here, since a bearing toward several things names no one.
		case 'intent_direction': {
			return {
				...baseCommand,
				direction: structure.directionToken.direction,
				...(destinationResolution === undefined || destinationResolution.setEntityRefs !== undefined ? {} : directObjectFieldOf(destinationResolution)),
			};
		}
		case 'intent_object': {
			return {
				...baseCommand,
				...directObjectFieldOf(requireResolution(objectResolution, 'objectResolution', structure.kind)),
			};
		}
		case 'intent_object_preposition': {
			return {
				...baseCommand,
				...directObjectFieldOf(requireResolution(objectResolution, 'objectResolution', structure.kind)),
				...boundRoleField(structure.preposition.bindsAs, requireResolution(boundResolution, 'boundResolution', structure.kind)),
			};
		}
		case 'intent_only': {
			return baseCommand;
		}
		case 'intent_preposition': {
			return {
				...baseCommand,
				...boundRoleField(structure.preposition.bindsAs, requireResolution(boundResolution, 'boundResolution', structure.kind)),
			};
		}
	}
};

/**
 * Map one structure diagnostic onto the public diagnostics contract.
 *
 * @param diagnostic - The grammar's teaching diagnostic
 * @param clauseTokens - The clause's canonical tokens (for `no_intent`, where every token went unplaced)
 * @param corrections - The clause's applied corrections (D26: failures quote ORIGINAL surfaces); defaults to none
 * @returns The public diagnostics
 */
export const buildStructureDiagnostics = (
	diagnostic: StructureDiagnostic,
	clauseTokens: readonly CanonicalToken[],
	corrections: readonly TokenCorrection[] = [],
): ParseDiagnostics => {
	switch (diagnostic.kind) {
		case 'dangling_preposition': {
			return {
				failureKind: 'structure_not_understood',
				unmatchedTokens: restoreOriginalSurfaces(
					[{ sourceSpan: diagnostic.preposition.sourceSpan, sourceText: diagnostic.preposition.word }],
					corrections,
				),
				...understoodIntentFields(diagnostic.intentToken),
			};
		}
		case 'empty_clause': {
			return { failureKind: 'blank_input', unmatchedTokens: []};
		}
		case 'no_intent': {
			return {
				failureKind: 'no_intent_recognized',
				unmatchedTokens: restoreOriginalSurfaces(clauseTokens, corrections),
			};
		}
		case 'object_forbidden': {
			return {
				failureKind: 'structure_not_understood',
				unmatchedTokens: restoreOriginalSurfaces(diagnostic.objectPhrase, corrections),
				...understoodIntentFields(diagnostic.intentToken),
			};
		}
		case 'object_required': {
			return {
				failureKind: 'structure_not_understood',
				unmatchedTokens: [],
				...understoodIntentFields(diagnostic.intentToken),
			};
		}
		case 'structure_not_understood': {
			return {
				failureKind: 'structure_not_understood',
				unmatchedTokens: restoreOriginalSurfaces([diagnostic.offendingToken], corrections),
				...understoodIntentFields(diagnostic.intentToken),
			};
		}
		case 'unbound_preposition': {
			return {
				failureKind: 'unbound_preposition',
				unmatchedTokens: restoreOriginalSurfaces(
					[{ sourceSpan: diagnostic.sourceSpan, sourceText: diagnostic.prepositionWord }],
					corrections,
				),
				...understoodIntentFields(diagnostic.intentToken),
			};
		}
	}
};

/**
 * The D24 context fields: what the grammar DID recognize before failing —
 * spread-friendly so absent intents add no keys (strict-object consumers).
 *
 * @param intentToken - The head intent in play, when one was recognized
 * @returns The optional fields to spread into the public diagnostics
 */
const understoodIntentFields = (
	intentToken: IntentCanonicalToken | undefined,
): Pick<ParseDiagnostics, 'understoodIntentObjectPolicy' | 'understoodIntentToken'> =>
	intentToken === undefined
		? {}
		: {
			understoodIntentObjectPolicy: intentToken.intent.objectPolicy,
			understoodIntentToken: intentToken.intent.canonicalToken,
		};

/**
 * The direct object's field: one ref, or — D217 — the set a plural named.
 * A set carries NO `directObjectRef`, so a consumer that has not learned
 * sets cannot mistake the first member for the whole: it meets a command
 * with no object and refuses it, rather than acting on one jar of four.
 *
 * @param resolution - The direct object's resolution
 * @returns The field to spread onto the command
 */
const directObjectFieldOf = (
	resolution: ResolvedEntityPhrase,
): Pick<ParsedCommand, 'directObjectRef' | 'directObjectSetRefs' | 'directObjectSurface'> =>
	resolution.setEntityRefs === undefined
		? { directObjectRef: resolution.entityRef, directObjectSurface: resolution.headSurface }
		: { directObjectSetRefs: resolution.setEntityRefs, directObjectSurface: resolution.headSurface };

/**
 * Place a bound ref on the field its intent declared, with the words the
 * player used for it beside it (D226, E20).
 *
 * @param bindsAs - The declared role
 * @param resolution - The resolved bound phrase
 * @returns The field fragment to spread into the command
 */
const boundRoleField = (
	bindsAs: 'indirect_object' | 'tool',
	resolution: ResolvedEntityPhrase,
): Pick<ParsedCommand, 'indirectObjectRef' | 'indirectObjectSurface' | 'toolRef' | 'toolSurface'> =>
	bindsAs === 'tool'
		? { toolRef: resolution.entityRef, toolSurface: resolution.headSurface }
		: { indirectObjectRef: resolution.entityRef, indirectObjectSurface: resolution.headSurface };

/**
 * Collect the pronoun resolutions a clause's phrases surfaced, object phrase
 * first (the player's reading order).
 *
 * @param resolutions - The clause's phrase resolutions
 * @returns The pronoun resolutions (often empty)
 */
const collectPronounResolutions = (resolutions: ClausePhraseResolutions): readonly PronounResolution[] =>
	[resolutions.objectResolution?.pronounResolution, resolutions.boundResolution?.pronounResolution].filter(
		(resolution): resolution is PronounResolution => resolution !== undefined,
	);

/**
 * The canonical intent a structure binds — the synthesized movement intent
 * for bare directions, the clause's own intent otherwise.
 *
 * @param structure - The matched structure
 * @returns The canonical intent token
 */
const intentTokenOf = (structure: ClauseStructure): IntentToken =>
	structure.kind === 'direction_only' ? structure.intent.canonicalToken : structure.intentToken.intent.canonicalToken;

/**
 * What the player TYPED for the verb (D36) — the matched surface, including
 * multiword aliases. A bare direction has no typed verb, so it reports the
 * synthesized movement intent's canonical token.
 *
 * D56: a bound particle rejoins the verb here, which is why it can be lifted
 * out of the clause without the refusal getting worse. "turn the kettle on"
 * and "switch it on" both surface as their verb plus `on`, so a refusal quotes
 * the phrasal the player meant rather than the bare half the grammar kept.
 *
 * @param structure - The matched structure
 * @returns The surface text consumers echo
 */
const intentSurfaceOf = (structure: ClauseStructure): string => {
	if (structure.kind === 'direction_only') {
		return structure.intent.canonicalToken;
	}

	const { particle } = particleFieldOf(structure);

	return particle === undefined ? structure.intentToken.sourceText : `${structure.intentToken.sourceText} ${particle}`;
};

/**
 * The particle field, present only when one was typed (D56).
 *
 * @remarks
 * The two DIRECTION arms are excluded by type rather than by check: a bare
 * direction and an `intent_direction` clause carve no post-verb phrase, so
 * there is nowhere a particle could have landed, and neither arm declares the
 * field. That is the narrowing this reads, not a defensive test.
 *
 * @param structure - The matched structure
 * @returns A spreadable field, empty when no particle bound
 */
const particleFieldOf = (structure: ClauseStructure): { readonly particle?: string } => {
	if (structure.kind === 'direction_only' || structure.kind === 'intent_direction' || structure.particle === undefined) {
		return {};
	}

	return { particle: structure.particle };
};

/**
 * Demand a resolution the structure's shape requires.
 *
 * @param resolution - The possibly-missing resolution
 * @param roleName - Which role was required (for the error message)
 * @param structureKind - The demanding structure's kind (for the error message)
 * @returns The resolution, narrowed
 * @throws Error when absent (programmer error — the orchestrator resolves every carved phrase before building)
 */
const requireResolution = (
	resolution: ResolvedEntityPhrase | undefined,
	roleName: string,
	structureKind: string,
): ResolvedEntityPhrase => {
	if (resolution === undefined) {
		throw new Error(`buildParsedCommand: structure "${structureKind}" requires ${roleName}, but none was provided`);
	}

	return resolution;
};
